// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/backgroundMusic.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/backgroundMusic.js
//
// Background music for invitations and cards: the Free To Use catalogue
// (https://freetouse.com/api), the shape a chosen track is saved in, and the
// arithmetic of looping one window of it.
//
// ── Why this provider ──────────────────────────────────────────────────────
//
// It is the one that allows what the feature is: music playing behind the
// host's own content. YouTube and Apple Music previews explicitly forbid that
// (YouTube's player must be visible; App Store Guideline 5.2.5 bans previews
// "as the background music to a photo collage"). Free To Use is a royalty-free
// library whose API needs no key and no login, returns each track's duration
// and a 300-point waveform (which is what the trimmer draws), and serves plain
// MP3s with open CORS and byte ranges, so both clients can seek and loop.
//
// ── Licence ────────────────────────────────────────────────────────────────
//
// The free licence is for non-commercial use and needs "the track title, the
// artist name and a link to Free To Use" wherever the music plays. Invits is a
// commercial product, so it needs Free To Use's Commercial Plan, which also
// lifts the attribution requirement. Until that is in place, invitations show
// the title and artist in their music line (see attributionLabel) and the
// premium tracks, which the free tier does not cover, are not offered.
//
// Pure: no imports, no I/O. Each client fetches the URLs built here itself.

/**
 * The whole feature, on or off. OFF: Free To Use's free licence does not cover
 * a commercial app, and no paid music licence is in place yet. While false the
 * Music option is hidden everywhere it would appear, and nothing plays even if
 * a row somehow holds a track. The picker, trimmer and playback are kept, and
 * are provider-agnostic apart from the URL builders below - swap those for a
 * licensed provider and flip this.
 */
export const BACKGROUND_MUSIC_ENABLED = false;

export const MUSIC_API_BASE = 'https://api.freetouse.com/v3';
export const MUSIC_PROVIDER = 'freetouse';
export const MUSIC_ATTRIBUTION_URL = 'https://freetouse.com/music';

/**
 * Premium tracks need a paid plan. Flip once the Commercial Plan is active and
 * its terms are confirmed to cover them.
 */
export const ALLOW_PREMIUM_TRACKS = false;

/** How long the loop can be, in seconds. The first is the default. */
export const LOOP_LENGTHS = [30, 15, 60, 90];
export const DEFAULT_LOOP_LENGTH = 30;
/** Shorter than this and a loop is a stutter, not music. */
export const MIN_LOOP_LENGTH = 10;

/**
 * The categories offered as chips, in the order an invitation host thinks in.
 * Names match Free To Use's own category names; clients resolve them to ids
 * with pickCategories once the category list has loaded. Any the catalogue
 * does not have are simply left out.
 */
export const MUSIC_CATEGORY_NAMES = [
  'Party', 'Wedding', 'Romantic', 'Happy', 'Corporate', 'Kids',
  'Uplifting', 'Calm', 'Emotional', 'Energetic', 'Christmas', 'Chill',
  'Dance', 'Jazz', 'Classical', 'Lofi', 'Inspiring', 'Peaceful',
];

/**
 * The category a picker should open on, from the invitation's event type.
 * A Wedding opens on Wedding, an Office Party on Corporate - the first thing
 * the host sees is already the right kind of music.
 */
export function categoryForEventType(eventType) {
  const t = String(eventType || '').toLowerCase();
  if (!t) return 'Happy';
  const rules = [
    [/wedding|engagement|reception|sangeet|mehendi|haldi|bridal|shaadi|nikah/, 'Wedding'],
    [/anniversary|valentine|date night|proposal/, 'Romantic'],
    [/office|corporate|conference|business|launch|meeting|networking|seminar|workshop/, 'Corporate'],
    [/baby|kid|child|toddler|naming|first birthday/, 'Kids'],
    [/christmas|xmas/, 'Christmas'],
    [/pooja|puja|prayer|katha|satsang|memorial|condolence/, 'Peaceful'],
    [/farewell|retirement|reunion/, 'Emotional'],
    [/graduation|convocation|award/, 'Inspiring'],
    [/dinner|cocktail|wine|brunch/, 'Jazz'],
    [/dance|club|dj|night/, 'Dance'],
    [/birthday|party|bbq|pool|beach|game|karaoke/, 'Party'],
    [/diwali|holi|eid|festival|navratri|garba|lohri|pongal|onam/, 'Happy'],
    [/housewarming|griha/, 'Uplifting'],
  ];
  for (const [re, name] of rules) if (re.test(t)) return name;
  return 'Happy';
}

// ── URLs ───────────────────────────────────────────────────────────────────

function qs(params) {
  const parts = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

export function categoriesUrl() {
  return `${MUSIC_API_BASE}/music/categories/all${qs({ limit: 0 })}`;
}

export function searchTracksUrl(query, { limit = 40, offset = 0 } = {}) {
  return `${MUSIC_API_BASE}/music/tracks/search${qs({
    query: String(query || '').trim(), limit, offset, order: 'plays', sort: 'desc',
  })}`;
}

export function categoryTracksUrl(categoryId, { limit = 40, offset = 0 } = {}) {
  return `${MUSIC_API_BASE}/music/categories/${encodeURIComponent(categoryId)}/tracks${qs({
    limit, offset, order: 'plays', sort: 'desc',
  })}`;
}

/** One track with everything (waveform included) - for re-trimming a saved choice. */
export function trackUrl(trackId) {
  return `${MUSIC_API_BASE}/music/tracks/${encodeURIComponent(trackId)}`;
}

/** Staff picks, for the picker before anything is chosen. */
export function featuredTracksUrl({ limit = 40 } = {}) {
  return `${MUSIC_API_BASE}/music/tracks/all${qs({ limit, order: 'staff_order', sort: 'desc' })}`;
}

// ── Catalogue data ─────────────────────────────────────────────────────────

/**
 * The provider's `artists` / `categories` fields are lists of [index, value]
 * pairs; this returns just the values.
 */
function pairValues(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((entry) => (Array.isArray(entry) ? entry[1] : entry))
    .filter((v) => v !== undefined && v !== null);
}

/** One API track as the pickers use it, or null if it cannot be played. */
export function normalizeTrack(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const url = raw.files && raw.files.mp3;
  const duration = Number(raw.duration);
  if (!raw.id || !url || !(duration > 0)) return null;

  const artists = pairValues(raw.artists).map((a) => (a && a.name) || '').filter(Boolean);
  const categories = pairValues(raw.categories).map((c) => (c && c.name) || '').filter(Boolean);
  const thumbs = raw.thumbnails || {};

  return {
    id: String(raw.id),
    title: String(raw.title || 'Untitled'),
    artist: artists.join(', ') || 'Unknown artist',
    artwork: thumbs.md || thumbs.sm || null,
    artworkSmall: thumbs.sm || thumbs.md || null,
    url: String(url),
    duration,
    waveform: Array.isArray(raw.waveform) ? raw.waveform.map((n) => Number(n) || 0) : [],
    isPremium: raw.is_premium === true,
    categories,
    plays: Number(raw.plays) || 0,
  };
}

/** Normalises a list response, dropping what cannot or may not be played. */
export function normalizeTrackList(response, { allowPremium = ALLOW_PREMIUM_TRACKS } = {}) {
  const data = response && Array.isArray(response.data) ? response.data : [];
  const out = [];
  const seen = new Set();
  for (const raw of data) {
    const t = normalizeTrack(raw);
    if (!t || seen.has(t.id)) continue;
    if (t.isPremium && !allowPremium) continue;
    seen.add(t.id);
    out.push(t);
  }
  return out;
}

/**
 * Our chip list, resolved against the provider's categories: [{ id, name }]
 * in MUSIC_CATEGORY_NAMES order, skipping names the catalogue lacks.
 */
export function pickCategories(response) {
  const data = response && Array.isArray(response.data) ? response.data : [];
  const byName = new Map();
  for (const c of data) {
    if (c && c.id && c.name) byName.set(String(c.name).toLowerCase(), { id: String(c.id), name: String(c.name) });
  }
  return MUSIC_CATEGORY_NAMES
    .map((name) => byName.get(name.toLowerCase()))
    .filter(Boolean);
}

// ── The loop window ────────────────────────────────────────────────────────

const round1 = (n) => Math.round(n * 10) / 10;

/**
 * A valid window inside a track: the length is at most the track, at least
 * MIN_LOOP_LENGTH (or the whole track if shorter), and the window never runs
 * past the end.
 */
export function clampWindow({ duration, start = 0, length = DEFAULT_LOOP_LENGTH }) {
  const d = Math.max(0, Number(duration) || 0);
  if (d === 0) return { start: 0, length: 0 };
  let len = Number(length) || DEFAULT_LOOP_LENGTH;
  len = Math.min(len, d);
  len = Math.max(len, Math.min(MIN_LOOP_LENGTH, d));
  let s = Math.max(0, Number(start) || 0);
  s = Math.min(s, d - len);
  return { start: round1(s), length: round1(len) };
}

/** The lengths worth offering for a track: those that fit, plus "whole track". */
export function loopLengthOptions(duration) {
  const d = Number(duration) || 0;
  const fits = LOOP_LENGTHS.filter((l) => l <= d);
  return fits.length ? fits : [round1(d)];
}

/** What gets saved on the invitation or card. */
export function toBackgroundMusic(track, { start = 0, length = DEFAULT_LOOP_LENGTH } = {}) {
  if (!track || !track.url) return null;
  const w = clampWindow({ duration: track.duration, start, length });
  return {
    provider: MUSIC_PROVIDER,
    trackId: track.id,
    title: track.title,
    artist: track.artist,
    artwork: track.artwork || null,
    url: track.url,
    duration: round1(Number(track.duration) || 0),
    start: w.start,
    length: w.length,
  };
}

/**
 * A saved value, validated - rows may hold null, a JSON string (some clients
 * serialise jsonb), or something malformed. Returns null for "no music".
 */
export function parseBackgroundMusic(value) {
  if (!BACKGROUND_MUSIC_ENABLED) return null;
  let v = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return null; }
  }
  if (!v || typeof v !== 'object' || !v.url) return null;
  const duration = Number(v.duration) || 0;
  const w = duration > 0
    ? clampWindow({ duration, start: v.start, length: v.length })
    : { start: Math.max(0, Number(v.start) || 0), length: Math.max(0, Number(v.length) || 0) };
  return {
    provider: v.provider || MUSIC_PROVIDER,
    trackId: v.trackId ? String(v.trackId) : null,
    title: String(v.title || 'Untitled'),
    artist: String(v.artist || ''),
    artwork: v.artwork || null,
    url: String(v.url),
    duration,
    start: w.start,
    length: w.length,
  };
}

/**
 * Where playback should jump, or null to carry on. Called on every time
 * update: past the end of the window (or before its start, after a stray
 * seek) it goes back to the start. A small lead keeps the jump inside the
 * window rather than letting the next bar's first note through.
 */
export function loopSeekTarget(currentTime, music, lead = 0.12) {
  if (!music || !(music.length > 0)) return null;
  const t = Number(currentTime) || 0;
  const end = music.start + music.length;
  if (t >= end - lead || t < music.start - 0.5) return music.start;
  return null;
}

/** m:ss */
export function formatClock(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** "Title · Artist" - the attribution line the free licence asks for. */
export function attributionLabel(music) {
  if (!music) return '';
  return music.artist ? `${music.title} · ${music.artist}` : music.title;
}

/**
 * The waveform squeezed (or stretched) to `bars` columns for drawing, each
 * 0..1. The API's is 300 points of 0..100 loudness.
 */
export function waveformBars(waveform, bars = 60) {
  const src = Array.isArray(waveform) && waveform.length ? waveform : [];
  if (!src.length) return new Array(bars).fill(0.35);
  const out = [];
  for (let i = 0; i < bars; i += 1) {
    const a = Math.floor((i / bars) * src.length);
    const b = Math.max(a + 1, Math.floor(((i + 1) / bars) * src.length));
    let peak = 0;
    for (let j = a; j < b && j < src.length; j += 1) peak = Math.max(peak, Number(src[j]) || 0);
    out.push(Math.max(0.08, Math.min(1, peak / 100)));
  }
  return out;
}
